import { SmartDisplayOutlined } from '@mui/icons-material';
import { Avatar, Box, Typography } from '@mui/material';
import { blue } from '@mui/material/colors';
import classSet from 'react-classset';
import { useSelector } from 'react-redux';

import type { VNSortableItemTemplateProps } from '@/components/VNSortable/VNSortableItem';
import type { OU } from '@/models/Ou';
import { applicationDataSelector } from '@/store/slices/application-data';
import { dateUtils } from '@/utils/dateUtils';

import AccessScheduleFormControls, { type ScheduleFormErrors } from './AccessScheduleFormControls';

type ScheduleFormValues = {
  from: string | null;
  fromDate: Date | null;
  to: string | null;
  toDate: Date | null;
  accessLevel: string;
};

type Props = VNSortableItemTemplateProps & {
  editingIndex?: number | null;
  scheduleForm?: ScheduleFormValues;
  fieldErrors?: ScheduleFormErrors;
  onScheduleFieldChange?: (field: string, value: string | Date) => void;
  onScheduleDateChange?: (field: string, value: string | Date) => void;
  onCancelScheduleEdit?: () => void;
  onSaveSchedule?: () => void;
};

const VNAccessScheduleCard = ({
  item,
  index,
  sortableId,
  editingIndex,
  scheduleForm,
  fieldErrors,
  onScheduleFieldChange,
  onScheduleDateChange,
  onCancelScheduleEdit,
  onSaveSchedule,
}: Props) => {
  const { ouData } = useSelector(applicationDataSelector);
  const isEditing = editingIndex === index;

  return (
    <Box
      className={classSet({
        'vn-access-schedule-card': true,
        active: !isEditing && item.isActive,
        controlling: !isEditing && item.isControlling,
        editing: isEditing,
      })}
      key={`${sortableId}`}
      display="flex"
      alignItems="center"
      p={2}
      borderRadius="12px"
      border="1px solid #ddd"
      sx={{ boxShadow: '0px 2px 4px rgba(0,0,0,0.1)', width: '100%', marginBottom: '8px' }}
    >
      {isEditing && scheduleForm && onScheduleFieldChange && onScheduleDateChange && onCancelScheduleEdit && onSaveSchedule ? (
        <AccessScheduleFormControls
          schedule={scheduleForm}
          ouData={ouData as OU}
          fieldErrors={fieldErrors}
          isUpdate
          onFieldChange={onScheduleFieldChange}
          onDateChange={onScheduleDateChange}
          onCancel={onCancelScheduleEdit}
          onSave={onSaveSchedule}
        />
      ) : (
        <>
          <Avatar
            sx={{
              bgcolor: blue[100],
              color: blue[700],
              width: 40,
              height: 40,
              mr: 2,
            }}
          >
            <SmartDisplayOutlined />
          </Avatar>
          <Box flex="1">
            <Typography variant="body1" fontWeight={500}>
              {item.from == null &&
                item.to != null &&
                `Before ${dateUtils.format(item.to, ouData.timezone)}`}
              {item.from != null &&
                item.to == null &&
                `After ${dateUtils.format(item.from, ouData.timezone)}`}
              {item.from != null &&
                item.to != null &&
                `${dateUtils.format(item.from, ouData.timezone)} to ${dateUtils.format(item.to, ouData.timezone)}`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.accessLevel || 'No Access'}
            </Typography>
          </Box>
          <div className="background-div" />
        </>
      )}
    </Box>
  );
};

export default VNAccessScheduleCard;
